import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

interface ProductDefect {
  id: string;
  product_id: string;
  defect_name: string;
  defect_code?: string;
  severity: number;
  description?: string;
  active: boolean;
}

interface ProductDefectsManagerProps {
  productId: string;
  productName: string;
  onClose: () => void;
}

const ProductDefectsManager: React.FC<ProductDefectsManagerProps> = ({
  productId,
  productName,
  onClose
}) => {
  const [defects, setDefects] = useState<ProductDefect[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDefect, setEditingDefect] = useState<string | null>(null);
  
  // Form state
  const [defectName, setDefectName] = useState('');
  const [defectCode, setDefectCode] = useState('');
  const [severity, setSeverity] = useState(5);
  const [description, setDescription] = useState('');

  useEffect(() => {
    fetchDefects();
  }, [productId]);

  const fetchDefects = async () => {
    try {
      const response = await fetch(`/api/products/${productId}/defects`);
      if (response.ok) {
        const data = await response.json();
        setDefects(data);
      }
    } catch (error) {
      console.error('Error fetching defects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDefect = async () => {
    try {
      const response = await fetch(`/api/products/${productId}/defects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          defectName,
          defectCode,
          severity,
          description
        })
      });

      if (response.ok) {
        await fetchDefects();
        resetForm();
        setShowAddForm(false);
      }
    } catch (error) {
      console.error('Error adding defect:', error);
    }
  };

  const handleUpdateDefect = async (defectId: string) => {
    const defect = defects.find(d => d.id === defectId);
    if (!defect) return;

    try {
      const response = await fetch(`/api/product-defects/${defectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          defectName: defect.defect_name,
          defectCode: defect.defect_code,
          severity: defect.severity,
          description: defect.description,
          active: defect.active
        })
      });

      if (response.ok) {
        setEditingDefect(null);
        await fetchDefects();
      }
    } catch (error) {
      console.error('Error updating defect:', error);
    }
  };

  const handleDeleteDefect = async (defectId: string) => {
    if (!confirm('Are you sure you want to delete this defect?')) return;

    try {
      const response = await fetch(`/api/product-defects/${defectId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchDefects();
      }
    } catch (error) {
      console.error('Error deleting defect:', error);
    }
  };

  const resetForm = () => {
    setDefectName('');
    setDefectCode('');
    setSeverity(5);
    setDescription('');
  };

  const getSeverityColor = (severity: number) => {
    if (severity >= 8) return 'bg-red-100 text-red-800';
    if (severity >= 5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">Defect Definitions</h2>
            <p className="text-gray-600">Product: {productName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Add New Defect Button/Form */}
        {!showAddForm ? (
          <button
            onClick={() => setShowAddForm(true)}
            className="mb-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add New Defect
          </button>
        ) : (
          <div className="mb-6 p-4 border rounded-lg bg-gray-50">
            <h3 className="font-semibold mb-4">Add New Defect</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Defect Name *
                </label>
                <input
                  type="text"
                  value={defectName}
                  onChange={(e) => setDefectName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Bruising"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code
                </label>
                <input
                  type="text"
                  value={defectCode}
                  onChange={(e) => setDefectCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., BR-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Severity (1-10) *
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={severity}
                    onChange={(e) => setSeverity(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className={`px-3 py-1 rounded-full font-semibold ${getSeverityColor(severity)}`}>
                    {severity}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Brief description"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowAddForm(false);
                  resetForm();
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDefect}
                disabled={!defectName || !severity}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
              >
                Add Defect
              </button>
            </div>
          </div>
        )}

        {/* Defects List */}
        {loading ? (
          <div className="text-center py-8">Loading defects...</div>
        ) : defects.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No defects defined for this product yet.
          </div>
        ) : (
          <div className="space-y-2">
            {defects.map((defect) => (
              <div
                key={defect.id}
                className="border rounded-lg p-4 hover:bg-gray-50"
              >
                {editingDefect === defect.id ? (
                  // Edit mode
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={defect.defect_name}
                      onChange={(e) => {
                        const updated = defects.map(d =>
                          d.id === defect.id ? { ...d, defect_name: e.target.value } : d
                        );
                        setDefects(updated);
                      }}
                      className="px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="text"
                      value={defect.defect_code || ''}
                      onChange={(e) => {
                        const updated = defects.map(d =>
                          d.id === defect.id ? { ...d, defect_code: e.target.value } : d
                        );
                        setDefects(updated);
                      }}
                      className="px-3 py-2 border rounded-lg"
                      placeholder="Code"
                    />
                    <div className="flex items-center space-x-2">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={defect.severity}
                        onChange={(e) => {
                          const updated = defects.map(d =>
                            d.id === defect.id ? { ...d, severity: parseInt(e.target.value) } : d
                          );
                          setDefects(updated);
                        }}
                        className="flex-1"
                      />
                      <span className={`px-3 py-1 rounded-full font-semibold ${getSeverityColor(defect.severity)}`}>
                        {defect.severity}
                      </span>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setEditingDefect(null)}
                        className="px-3 py-1 border rounded hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleUpdateDefect(defect.id)}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  // View mode
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4">
                        <span className="font-semibold">{defect.defect_name}</span>
                        {defect.defect_code && (
                          <span className="text-gray-500 text-sm">({defect.defect_code})</span>
                        )}
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getSeverityColor(defect.severity)}`}>
                          Severity: {defect.severity}
                        </span>
                      </div>
                      {defect.description && (
                        <p className="text-gray-600 text-sm mt-1">{defect.description}</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingDefect(defect.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteDefect(defect.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDefectsManager;